// Impact page content — figures sourced from worldfarmers.org (About + Markets
// pages) and the homepage credibility stats. Annual report links point to the
// existing reports published on worldfarmers.org; years without a published
// PDF are marked `href: null` and render as "Coming soon".

export const IMPACT_HERO = {
  eyebrow: "",
  title: "Impact",
  lede: "Putting farmers first.",
  image: "/images/wf/farm-159.jpg",
  imageAlt:
    "A farmer wearing a wide-brimmed hat bends over rows of crops at golden hour.",
};

// Headline figures (expanded from the homepage stat row).
export const IMPACT_STATS = [
  {
    id: "farmers",
    value: "275",
    label: "Farmers in our Program",
    detail:
      "Living across over 40 towns and cities in Massachusetts, Rhode Island, and New Hampshire, farmers travel far to access land and our program offerings.",
  },
  {
    id: "countries",
    value: "25",
    label: "Countries Represented",
    detail:
      "Farmers in our program come from many parts of the world, representing 25 countries of origin and nine of the 22 sub-regions of the World.",
  },
  {
    id: "acres",
    value: "135",
    label: "Acres in Production",
    detail:
      "World Farmers operates nine program landsites. Two are accessible to farmers across all phases of development, and the remaining support commercial farmers to scale.",
  },
  {
    id: "communities",
    value: "140+",
    label: "Communities Served",
    detail:
      "Only a snapshot of a true figure, this number represents farmers\u2019 hometowns, and communities reached through both World Farmers and program farmers\u2019 markets.",
  },
];

// From plot to independence.
export const IMPACT_INDEPENDENCE = {
  eyebrow: "From plot to independence",
  title: "Nearly one in three farmers goes on to farm their own land",
  body:
    "To date, of the over 1,200 farmers who have had plots at Flats Mentor Farm, 30% have gone on to farm their own land \u2014 60% of whom are women. These independent farmers have established their operations across the country, from North Carolina to California.",
  figures: [
    { id: "own-land", value: "30%", label: "Have gone on to farm their own land" },
    { id: "women-independent", value: "60%", label: "Of those independent farmers are women" },
    { id: "reach", value: "NC \u2192 CA", label: "Independent farms established coast to coast" },
  ],
  image: "/images/wf/farm-fabiola.jpg",
  imageAlt:
    "A farmer in colorful traditional clothing harvesting greens in an open field under a dramatic sky.",
};

// A national model.
export const IMPACT_MODEL = {
  eyebrow: "A national model",
  title: "Recognized by the USDA since 1999",
  body:
    "In 1999, Flats Mentor Farm was identified as a national model by the USDA and has served as a model for many beginning-farmer programs nationally. Our five-phase mentorship path \u2014 from first-year subsistence growing to fully independent farm businesses \u2014 has been studied and adapted by programs across the country.",
  points: [
    "Hands-on training in production practices and market development",
    "One-on-one technical assistance in farmer access to external resources",
    "Continuous support in business development and financial literacy",
    "Cross-cultural co-learning among farmers and staff",
  ],
  image: "/images/wf/vlai/vlai-3875.jpg",
  imageAlt:
    "A large hoop house stands beside rows of crops under a blue summer sky.",
};

// Who farms at the Flats.
export const IMPACT_COMMUNITY = {
  eyebrow: "Who farms at the Flats",
  title: "A community from every corner of the globe",
  body:
    "Farmers from any corner of the globe are welcome at Flats Mentor Farm. Most farmers learn about the opportunity via word of mouth, which ensures that everyone at the Flats has at least one point of contact with their fellow farmers.",
  facts: [
    { id: "africa", value: "Over half", label: "of our farmers are from Central and East Africa" },
    { id: "asia", value: "Longest tenure", label: "farmers are of Southeast Asian origin" },
    { id: "women", value: "Two-thirds", label: "of our farmers are women" },
  ],
  image: "/images/wf/farmer6.jpg",
  imageAlt:
    "A farmer smiling broadly while standing in a sunlit field of crops.",
};

// Annual reports — newest first. `href: null` renders a "Coming soon" card.
export const ANNUAL_REPORTS = [
  { year: "2025", title: "2025 Annual Report", href: null, type: "pdf" },
  { year: "2024", title: "2024 Annual Report", href: null, type: "pdf" },
  { year: "2023", title: "2023 Annual Report", href: "https://www.worldfarmers.org/world-farmers-2023-annual_report/", type: "web" },
  { year: "2022", title: "2022 Annual Report", href: "https://www.worldfarmers.org/world-farmers-2022-annual_report/", type: "web" },
  { year: "2021", title: "2021 Annual Report", href: "https://www.worldfarmers.org/world-farmers-2021-annual_report/", type: "web" },
  { year: "2020", title: "2020 Annual Report", href: "https://www.worldfarmers.org/world-farmers-2020-annual_report/", type: "web" },
  { year: "2019", title: "2019 Annual Report", subtitle: "4th Annual Harvest Gala & Cultural Tasting", href: "https://www.worldfarmers.org/wp-content/uploads/2021/02/WF_Program_2019.pdf", type: "pdf" },
  { year: "2018", title: "2018 Annual Report", subtitle: "3rd Annual Harvest Gala", href: "https://www.worldfarmers.org/wp-content/uploads/2021/02/WF_Program_2018.pdf", type: "pdf" },
  { year: "2017", title: "2017 Annual Report", subtitle: "2nd Annual Harvest Gala", href: "https://www.worldfarmers.org/wp-content/uploads/2021/02/WF_Program_2017.pdf", type: "pdf" },
  { year: "2016", title: "2016 Annual Report", subtitle: "Harvest Gala", href: "https://www.worldfarmers.org/wp-content/uploads/2021/02/WF_Program_2016.pdf", type: "pdf" },
];

export const IMPACT_REPORTS_INTRO = {
  eyebrow: "Annual reports",
  title: "Read the full story, year by year",
  body:
    "Our annual reports share the season\u2019s harvest, program outcomes, financials, and the farmers and partners who made it possible.",
};
